import { Skeleton } from "@-/dev.improve.haniverse.components.ui.skeleton";
import filledArray from "@-/com.npmjs.filled-array";
import split from "@-/com.npmjs.just-split";
import { View } from "@-/com.npmjs.react-native";

/**
 * Placeholder tiles for the due-review grid, shown while the revealed page's tile data (SRS stage +
 * pinyin slice) is still being read.
 *
 * Why a skeleton rather than the real tiles with neutral placeholders: the tile data is a paged
 * actor + content read, and until it lands every tile paints as an `unadded` glyph with no pinyin
 * dots — a *wrong* rendering of a real character, which then visibly flips stage colour and grows a
 * dots row when its page arrives. A skeleton says "loading" honestly and swaps to the finished grid
 * in one step instead of two.
 *
 * The geometry is the grid's own — same column count, same gap, same square tiles — so the swap is
 * a content change, not a layout jump.
 */
export function LearnSummaryDueReviewGridSkeleton({
  columns,
  count,
  gap,
}: {
  columns: number;
  /** How many placeholder tiles to draw — the revealed window's size. */
  count: number;
  gap: number;
}) {
  const rows = split(
    filledArray((index) => index, count),
    columns,
  );

  return (
    <View
      accessibilityLabel="Loading reviews"
      style={{ gap }}
      testID="learn-summary-due-review-grid-skeleton"
    >
      {rows.map((row, rowIndex) => (
        <View className="flex flex-row" key={rowIndex} style={{ gap }}>
          {row.map((tileIndex) => (
            <View className="flex-1" key={tileIndex}>
              <Skeleton className="aspect-square w-full" />
            </View>
          ))}
          {filledArray(
            (placeholderIndex) => (
              <View className="flex-1" key={`placeholder-${rowIndex}-${placeholderIndex}`} />
            ),
            columns - row.length,
          )}
        </View>
      ))}
    </View>
  );
}
